import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { getMetricTip, type MetricTipId } from "@/lib/metricsGlossary";

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 max-w-xs rounded-md border border-border bg-card px-3 py-2 text-xs leading-relaxed text-card-foreground shadow-lg",
        "opacity-100 backdrop-blur-none",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        className,
      )}
      {...props}
      style={{
        backgroundColor: "hsl(var(--card))",
        color: "hsl(var(--card-foreground))",
        ...(typeof props.style === "object" && props.style ? props.style : {}),
      }}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/** Ikonka Info z definicją ze słowniczka (hover / tap). */
export function InfoTip({
  tipId,
  text,
  className,
}: {
  tipId?: MetricTipId;
  text?: string;
  className?: string;
}) {
  const tip = tipId ? getMetricTip(tipId) : null;
  const body = text || tip?.long || tip?.short;
  if (!body) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              className,
            )}
            aria-label={tip?.label ? `Co znaczy: ${tip.label}` : "Wyjaśnienie"}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {tip?.label && <div className="mb-1 font-medium text-foreground">{tip.label}</div>}
          <div>{body}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Etykieta + opcjonalna ikonka wyjaśnienia. */
export function LabelWithTip({
  tipId,
  label,
  className,
}: {
  tipId?: MetricTipId;
  label?: string;
  className?: string;
}) {
  const tip = tipId ? getMetricTip(tipId) : null;
  const shown = label ?? tip?.label ?? "";
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <span>{shown}</span>
      {(tipId || tip) && <InfoTip tipId={tipId} />}
    </span>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
